import ItemCard from '@/features/menu/menu-item';
import { ScrollState } from '@/pages/welcome';
import { Link } from '@inertiajs/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { RefObject } from 'react';
import { Button } from './ui/button';

const FeaturedSections = ({
    sections,
    scrollState,
    scrollToStart,
    scrollToEnd,
    scrollRefs,
    updateScrollState,
}: {
    sections: App.Data.HomepageSectionData[];
    scrollState: Record<number, ScrollState>;
    scrollToStart: (id: number) => void;
    scrollToEnd: (id: number) => void;
    scrollRefs: RefObject<Record<number, HTMLDivElement | null>>;
    updateScrollState: (id: number) => void;
}) => {
    return (
        <div className="container mx-auto px-6 py-10">
            {sections.map((section) => {
                // Skip rendering if section or its id is invalid
                if (!section || section.id == null) {
                    return null;
                }

                const state = scrollState[section.id] ?? {
                    atStart: true,
                    atEnd: false,
                };

                return (
                    <div key={section.id} className="mb-12">
                        {/* Header + buttons */}
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold md:text-3xl text-primary">
                                    {section.title}
                                </h2>
                                {section.content && (
                                    <p className="mt-1 text-gray-600">
                                        {section.content}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Nav buttons */}
                        <div className="mb-4 flex items-center">
                            {section.hasButton !== 0 && section.link && (
                                <Button className="mt-2">
                                    <Link href={section.link} prefetch={true} preserveScroll={false}>View All</Link>
                                </Button>
                            )}
                            <div className="ml-auto flex gap-2">
                                {/* Start button */}
                                <button
                                    disabled={state.atStart}
                                    onClick={() => scrollToStart(section.id)}
                                    className={`rounded-full p-3 transition ${
                                        state.atStart
                                            ? 'cursor-not-allowed bg-gray-300'
                                            : 'bg-primary hover:bg-primary/90'
                                    } `}
                                >
                                    <ChevronLeftIcon
                                        className={`h-5 w-5 ${
                                            state.atStart
                                                ? 'text-gray-500'
                                                : 'text-white'
                                        }`}
                                    />
                                </button>

                                {/* End button */}
                                <button
                                    disabled={state.atEnd}
                                    onClick={() => scrollToEnd(section.id)}
                                    className={`rounded-full p-3 transition ${
                                        state.atEnd
                                            ? 'cursor-not-allowed bg-gray-300'
                                            : 'bg-primary hover:bg-primary/90'
                                    } `}
                                >
                                    <ChevronRightIcon
                                        className={`h-5 w-5 ${
                                            state.atEnd
                                                ? 'text-gray-500'
                                                : 'text-white'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>
                        {/* Horizontal list */}
                        <div
                            ref={(el) => {
                                if (section.id != null) {
                                    scrollRefs.current[section.id] = el;
                                }
                            }}
                            onScroll={() => {
                                if (section.id != null) {
                                    updateScrollState(section.id);
                                }
                            }}
                            className="no-scrollbar flex gap-6 overflow-x-auto"
                        >
                            {section.menu_items?.map((item) => {
                                if (!item || item.id == null) {
                                    return null;
                                }
                                return (
                                    <div
                                        className="min-w-[300px] sm:min-w-[350px] max-w-[350px]"
                                        key={item.id}
                                    >
                                        <ItemCard item={item} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FeaturedSections;
